import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../../src/App.js";
import { useDropzone } from "react-dropzone";
import { Plus, Upload, ChevronLeft, ChevronRight, Trash2, Edit2, Image } from "lucide-react";
import { Loader } from "../../../components/Loader";

export const ColorsPage = () => {
  const [colors, setColors] = useState<any[]>([]);
  const [totalColors, setTotalColors] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    colorImage: "",
  });

  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const [image, setImage] = useState<File | null>(null);
  const maxResults = 12;

  // Dropzone hook
  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*" as any,
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      setImage(file);
      convertToBase64(file);
    },
  });

  // Convert the image to base64
  const convertToBase64 = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setFormData((prevData) => ({
        ...prevData,
        colorImage: base64String,
      }));
    };
    reader.readAsDataURL(file);
  };

  // Fetch colors from API
  const fetchColors = async (page = 1) => {
    try {
      const firstResults = (page - 1) * maxResults;
      const response = await axios.get(`${API_BASE_URL}account/${id}/colors`, {
        params: { firstResults, maxResults },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching colors:", error);
      return { colors: [], totalCount: 0 };
    }
  };

  // Create or Update color
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await updateColor(formData.id, formData);
      } else {
        const newColor = await createColor(formData);
        setColors([...colors, newColor]);
      }
      const loadColors = async () => {
        const result = await fetchColors(page);
        setColors(result.colors);
        setTotalColors(result.totalCount);
      };
      loadColors();
      setIsDrawerOpen(false);
      resetForm();
    } catch (error) {
      console.error("Failed to submit color:", error);
    }
  };

  // Create color API call
  const createColor = async (colorData: any) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}account/${id}/color/create`,
        {
          name: colorData?.name,
          colorImage: colorData?.colorImage,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating color:", error);
      throw error;
    }
  };

  // Update color API call
  const updateColor = async (colorId: string, colorData: any) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}account/${id}/color/update`,
        {
          id: colorId,
          colorImage: colorData?.colorImage,
          ...colorData,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating color:", error);
      throw error;
    }
  };

  // Delete color
  const handleDelete = async (colorId: string) => {
    if (window.confirm("Are you sure you want to delete this color?")) {
      try {
        await deleteColor(colorId);
        setColors(colors.filter((color) => color.id !== colorId));
      } catch (error) {
        console.error("Failed to delete color:", error);
      }
    }
  };

  // Delete color API call
  const deleteColor = async (colorId: string) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}account/${id}/color/${colorId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting color:", error);
      throw error;
    }
  };

  useEffect(() => {
    const loadColors = async () => {
      setLoading(true);
      try {
        const result = await fetchColors(page);
        setColors(result.colors);
        setTotalColors(result.totalCount);
      } finally {
        setLoading(false);
      }
    };
    loadColors();
  }, [page]);

  // Open the drawer in create mode
  const openDrawerForCreate = () => {
    setFormData({
      id: "",
      name: "",
      colorImage: "",
    });
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  // Open the drawer in edit mode
  const openDrawerForEdit = (color: any) => {
    setFormData(color);
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setImage(null);
    setFormData({
      id: "",
      name: "",
      colorImage: "",
    });
  };

  const totalPages = Math.ceil(totalColors / maxResults);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-2xl">🎨</span>
            Colors
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage color samples and palettes</p>
        </div>
        <Button
          onClick={openDrawerForCreate}
          className="btn-primary flex items-center gap-2 w-full sm:w-auto"
        >
          <Plus size={18} />
          New Color
        </Button>
      </div>

      {/* Colors Grid - Desktop and Mobile Responsive */}
      {colors.length === 0 ? (
        <div className="py-12 text-center bg-white rounded-lg border border-gray-200">
          <Image className="mx-auto mb-4 text-gray-300" size={48} />
          <p className="text-gray-600 font-medium">No colors found</p>
          <p className="text-gray-400 text-sm mt-1">Create a new color to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {colors.map((color) => (
            <div
              key={color.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Color Image */}
              <div className="w-full h-40 bg-gray-100 overflow-hidden flex items-center justify-center">
                {color.colorImage ? (
                  <img
                    src={color.colorImage}
                    alt={color.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <Image className="text-gray-400" size={32} />
                  </div>
                )}
              </div>

              {/* Color Details */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-3 truncate">{color.name}</h3>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => openDrawerForEdit(color)}
                    className="flex-1 btn-primary"
                  >
                    <Edit2 size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(color.id)}
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong> ({<strong>{totalColors}</strong>} total colors)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Drawer Form */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-96 md:h-auto max-h-screen md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-lg ${
          isDrawerOpen ? "translate-y-0 md:scale-100" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {isCreating ? "Create Color" : "Edit Color"}
          </h2>
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-gray-700 mb-2 block">Color Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter color name (e.g., Midnight Blue)"
              className="w-full"
              required
            />
          </div>

          <div>
            <Label className="text-gray-700 mb-2 block">Color Image</Label>
            <div
              {...getRootProps()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-[var(--accent-primary)] hover:bg-[var(--sidebar-light)] transition-colors text-center"
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto mb-2 text-gray-400" size={32} />
              {!image && !formData.colorImage ? (
                <div>
                  <p className="text-gray-600 font-medium">Drag and drop your image here</p>
                  <p className="text-gray-400 text-sm">or click to select a file</p>
                </div>
              ) : (
                <p className="text-green-600 font-medium">✓ Image Selected</p>
              )}
            </div>

            {/* Image Preview */}
            {(image || formData.colorImage) && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Preview</p>
                <img
                  src={image ? URL.createObjectURL(image) : formData.colorImage}
                  alt="Preview"
                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                />
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="submit"
              className="flex-1 btn-primary"
            >
              {isCreating ? "Create" : "Update"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDrawerOpen(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ColorsPage;
